import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: 'user' | 'admin' | 'super_admin';
  status: 'active' | 'suspended' | 'deleted';
  plan: 'free' | 'single' | 'family' | 'pro';
  credits: number;
  reports_used: number;
  paymentStatus: 'none' | 'paid';
  stripeCustomerId?: string | null;
  lastStripeSessionId?: string | null;
  purchasedAt?: any | null;
  updatedAt?: any | null;
  proStatus: 'active' | 'inactive';
  proGrantedBy?: string | null;
  proGrantedAt?: any | null;
  proExpiresAt?: any | null;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  maintenanceMode: boolean;
  refreshProfile: () => Promise<UserProfile | null>;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  profile: null, 
  loading: true, 
  maintenanceMode: false,
  refreshProfile: async () => null 
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  const refreshProfile = async (): Promise<UserProfile | null> => {
    if (auth.currentUser) {
      try {
        const docRef = doc(db, 'users', auth.currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const newProfile = docSnap.data() as UserProfile;
          setProfile(newProfile);
          return newProfile;
        }
      } catch (error) {
        console.error("Failed to refresh user profile", error);
      }
    }
    return null;
  };

  useEffect(() => {
    // Fetch system settings independently of auth
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'system_config', 'main');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          setMaintenanceMode(settingsSnap.data().maintenanceMode || false);
        }
      } catch (e) {
        console.error("Failed to fetch system settings", e);
      }
    };
    fetchSettings();

    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser: User | null) => {
      setUser(firebaseUser);
      
      // Clean up previous profile listener if any
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (firebaseUser) {
        const docRef = doc(db, 'users', firebaseUser.uid);
        unsubscribeProfile = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            setProfile(docSnap.data() as UserProfile);
          } else {
            setProfile(null);
          }
          setLoading(false);
        }, (error) => {
          console.error("Failed to fetch user profile in real-time", error);
          setProfile(null);
          setLoading(false);
        });
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, loading, maintenanceMode, refreshProfile }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
